import { Download } from "@mui/icons-material";
import {
  DialogActions,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { AI_PDF_VIEWER_ALLOW_EXCEL_DOWNLOADS } from "flags";
import React, { useState } from "react";
import { useFeatureFlag } from "services/staticDataService";

/**
 * Calculate table statistics for display in footer
 */
const calculateTableStats = (tables) => {
  const nonSeparatorTables = tables.filter((t) => !t.isSeparator);
  const combinedTables = nonSeparatorTables.filter((t) => t.group_name);
  const individualTables = nonSeparatorTables.filter((t) => !t.group_name);

  if (combinedTables.length > 0 && individualTables.length > 0) {
    const totalInCombined = combinedTables.reduce(
      (sum, t) => sum + (t.table_count || 1),
      0
    );
    return `Groups: ${combinedTables.length} (${totalInCombined} tables) + ${individualTables.length} individual`;
  } else if (combinedTables.length > 0) {
    const totalInCombined = combinedTables.reduce(
      (sum, t) => sum + (t.table_count || 1),
      0
    );
    return `Groups: ${combinedTables.length} (${totalInCombined} total tables)`;
  } else {
    return `Tables: ${nonSeparatorTables.length}`;
  }
};

/**
 * PdfViewerDialogFooter - Footer component for PDF Table Viewer dialog
 * Shows table statistics and action buttons (Cancel/Continue)
 */
const PdfViewerDialogFooter = ({
  tables,
  selectedTable,
  onCancel,
  onContinue,
  onDownloadExcel,
}) => {
  const tableStats = calculateTableStats(tables);
  const excelDownloadEnabled = useFeatureFlag(
    AI_PDF_VIEWER_ALLOW_EXCEL_DOWNLOADS
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTablesForExport, setSelectedTablesForExport] = useState([]);
  const open = Boolean(anchorEl);

  const handleDownloadClick = (event) => {
    // Initialize selection with only the current table selected
    const currentTableId = selectedTable.table_id || selectedTable.id;
    setSelectedTablesForExport([currentTableId]);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleTable = (tableId) => {
    setSelectedTablesForExport((prev) => {
      if (prev.includes(tableId)) {
        return prev.filter((id) => id !== tableId);
      } else {
        return [...prev, tableId];
      }
    });
  };

  const handleExportConfirm = () => {
    if (onDownloadExcel) {
      onDownloadExcel(selectedTablesForExport);
    }
    handleClose();
  };

  return (
    <DialogActions
      sx={{
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 },
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 2, sm: 0 },
        borderTop: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        position: "sticky",
        bottom: 0,
        zIndex: 1,
      }}
    >
      <Typography
        variant={"body2"}
        color={"text.secondary"}
        sx={{
          order: { xs: 2, sm: 1 },
          textAlign: { xs: "center", sm: "left" },
          fontSize: { xs: "0.75rem", sm: "0.875rem" },
        }}
      >
        {tableStats}
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1, sm: 1.5 },
          order: { xs: 1, sm: 2 },
          width: { xs: "100%", sm: "auto" },
          justifyContent: { xs: "stretch", sm: "flex-end" },
        }}
      >
        {excelDownloadEnabled && (
          <>
            <Button
              onClick={handleDownloadClick}
              startIcon={<Download />}
              variant={"contained"}
              color={"primary"}
              sx={{
                flex: { xs: 1, sm: "0 0 auto" },
                minWidth: { xs: "auto", sm: "140px" },
                px: { xs: 2, sm: 3 },
              }}
            >
              {"EXPORT EXCEL"}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              PaperProps={{
                style: {
                  width: 320,
                },
              }}
            >
              <Box sx={{ p: 2, pb: 1, borderBottom: "1px solid #eee" }}>
                <Typography variant={"subtitle2"} sx={{ fontWeight: 600 }}>
                  {"Select Tables to Export"}
                </Typography>
              </Box>
              <Box
                sx={{
                  maxHeight: 300,
                  overflowY: "auto",
                }}
              >
                {tables.map((table, index) => {
                  if (table.isSeparator) return null;

                  const tableId = table.table_id || table.id;

                  // Calculate proper index for display
                  const separatorIndex = tables.findIndex((t) => t.isSeparator);
                  const isIndividualTable =
                    separatorIndex >= 0 && index > separatorIndex;
                  const displayIndex = isIndividualTable
                    ? index - separatorIndex
                    : index + 1;

                  const tableName =
                    !isIndividualTable && displayIndex === 1
                      ? "Combined Table"
                      : `Table ${displayIndex}`;

                  const label = table.group_name
                    ? `${table.group_name} (${table.table_count || 1} tables, ${
                        table.rows?.length || 0
                      } rows)`
                    : `${tableName} (${table.rows?.length || 0} rows)`;

                  return (
                    <MenuItem
                      key={tableId}
                      onClick={() => handleToggleTable(tableId)}
                      dense
                    >
                      <Checkbox
                        checked={selectedTablesForExport.includes(tableId)}
                        size={"small"}
                      />
                      <ListItemText
                        primary={label}
                        primaryTypographyProps={{
                          fontSize: "0.8125rem",
                        }}
                      />
                    </MenuItem>
                  );
                })}
              </Box>
              <Box
                sx={{
                  p: 1.5,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  borderTop: "1px solid #eee",
                  backgroundColor: "background.paper",
                }}
              >
                <Button
                  onClick={handleClose}
                  size={"small"}
                  color={"secondary"}
                >
                  {"Cancel"}
                </Button>
                <Button
                  onClick={handleExportConfirm}
                  size={"small"}
                  variant={"contained"}
                  disabled={selectedTablesForExport.length === 0}
                >
                  {"Export"}
                </Button>
              </Box>
            </Menu>
          </>
        )}

        <Button
          onClick={onCancel}
          disableElevation
          variant={"contained"}
          color={"secondary"}
          sx={{
            flex: { xs: 1, sm: "0 0 auto" },
            minWidth: { xs: "auto", sm: "100px" },
            px: { xs: 2, sm: 3 },
          }}
        >
          {"CANCEL"}
        </Button>
        <Button
          variant={"contained"}
          onClick={onContinue}
          sx={{
            flex: { xs: 1, sm: "0 0 auto" },
            minWidth: { xs: "auto", sm: "120px" },
            px: { xs: 2, sm: 3 },
          }}
        >
          {process.env.NEXT_PUBLIC_APP_MODE === "standalone"
            ? "SAVE RESULTS"
            : "CONTINUE"}
        </Button>
      </Box>
    </DialogActions>
  );
};

export default PdfViewerDialogFooter;
